// -- [URL hash parsing and building for role/topic - shareable links and back button]

import type { ContentConfig, RoleKey } from '@/types/content'

const ROLE_KEY_TO_SLUG: Record<RoleKey, string> = {
  service: 'service',
  sales: 'sales',
  finance: 'finance',
  management: 'dealer_principal',
}

const SLUG_TO_ROLE_KEY: Record<string, RoleKey> = {
  service: 'service',
  sales: 'sales',
  finance: 'finance',
  dealer_principal: 'management',
  management: 'management',
}

export interface HashParams {
  roleSlug?: string
  topicSlug?: string
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\w\-_]/g, '')
}

/** Parse hash segments into role slug and topic slug (ignores unknown params e.g. legacy version=) */
export function parseHashParams(hash: string): HashParams {
  const hashPart = hash.replace(/^#/, '')
  if (!hashPart) return {}

  const result: HashParams = {}
  const parts = hashPart.split('&')
  for (const p of parts) {
    if (p.startsWith('role-') || p.startsWith('role_')) {
      result.roleSlug = p.replace(/^role[-_]/, '')
    } else if (p.startsWith('topic=')) {
      result.topicSlug = decodeURIComponent(p.slice(6))
    }
  }
  return result
}

/** Parse hash #role-sales&topic=deal-packs or search ?topic=deal-packs&role=sales. Returns null if nothing usable. */
export function parseRoleTopicFromUrl(config: ContentConfig): {
  roleKey: RoleKey
  topicKey: string
} | null {
  const hash = typeof window !== 'undefined' ? window.location.hash : ''
  const search = typeof window !== 'undefined' ? window.location.search : ''
  if (!hash && !search) return null

  const { roleSlug: hashRoleSlug, topicSlug: hashTopicSlug } = parseHashParams(hash)
  let roleSlug: string | null = hashRoleSlug ?? null
  let topicSlug: string | null = hashTopicSlug ?? null

  // -- [Parse search: ?topic=deal-packs&role=sales]
  if (search) {
    const params = new URLSearchParams(search)
    if (params.get('topic')) topicSlug = params.get('topic')
    if (params.get('role')) roleSlug = params.get('role')
  }

  if (!roleSlug && !topicSlug) return null

  let roleKey: RoleKey | null = roleSlug
    ? (SLUG_TO_ROLE_KEY[roleSlug.toLowerCase()] ??
        (config.roles[roleSlug as RoleKey] ? (roleSlug as RoleKey) : null))
    : null

  // -- [If topic given but no role, find which role has that topic]
  if (!roleKey && topicSlug) {
    for (const rk of Object.keys(config.roles) as RoleKey[]) {
      const topics = config.roles[rk].topics ?? []
      const found =
        topics.find((t) => t.key === topicSlug) ??
        topics.find((t) => slugify(t.label) === topicSlug.toLowerCase().replace(/-/g, '_'))
      if (found) {
        roleKey = rk
        break
      }
    }
  }

  if (!roleKey || !config.roles[roleKey]) return null

  const topics = config.roles[roleKey].topics ?? []
  let topicKey: string | null = null
  if (topicSlug) {
    topicKey = topics.find((t) => t.key === topicSlug)?.key ?? null
    if (!topicKey) {
      const slug = topicSlug.toLowerCase().replace(/-/g, '_')
      const byLabel = topics.find((t) => slugify(t.label) === slug)
      if (byLabel) topicKey = byLabel.key
    }
  }

  if (!topicKey) topicKey = topics[0]?.key ?? ''

  return { roleKey, topicKey }
}

/** Build hash #role-sales&topic=deal-packs for shareable links */
export function buildRoleTopicHash(roleKey: RoleKey, topicKey: string): string {
  const roleSlug = ROLE_KEY_TO_SLUG[roleKey] ?? roleKey
  return `#role-${roleSlug}&topic=${encodeURIComponent(topicKey)}`
}
